/**
 * Axios Integration Layer
 *
 * This module provides seamless integration between Axios and the unified request
 * coordinator system. It replaces the traditional interceptor approach with a
 * centralized coordination system that manages retries, authentication, error
 * handling, and metrics as a unified whole.
 */

import { getObservability } from '../../../di/container';
import { getSessionEpoch } from '../../../store/session-scope';
import { SessionEndedError } from '../../../types/api/errors';
import {
  DEFAULT_COORDINATOR_CONFIG,
  type AxiosInstanceWithOriginal,
  type AxiosIntegrationConfig,
  type AxiosIntegrationContext,
} from '../../../types/api/integration.types';
import { ObservabilitySeverity } from '../../../types/observability';
import { dayjs } from '../../../utils/date';
import { getEnv, appEnv } from '../../../utils/env';
import { createRequestCoordinator } from '../core/request.coordinator';

import type {
  RequestConfig,
  RequestContext,
  IntegrationConfig,
  IAxiosIntegration,
  IAxiosIntegrationMetrics,
  ICoordinatorConfig,
  IRequestCoordinator,
  CoordinatorConfig,
  IAuthIntegration,
} from '../../../types/api';
import type {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

// =============================================================================
// INTEGRATION CONFIGURATION
// =============================================================================

const DEFAULT_INTEGRATION_CONFIG: IntegrationConfig = {
  preserveAuthInterceptor: true,
  preservePerformanceMonitoring: true,
  preserveNotificationSystem: true,
  enableLegacyCompatibility: false, // User said no backward compatibility needed
};

const DEFAULT_AXIOS_INTEGRATION_CONFIG: AxiosIntegrationConfig = {
  ...DEFAULT_COORDINATOR_CONFIG,
  integration: DEFAULT_INTEGRATION_CONFIG,
  enableLegacyMode: false,
  debug: appEnv('development'),
};

// =============================================================================
// MAIN INTEGRATION CLASS
// =============================================================================

/**
 * Axios Integration Layer implementation.
 *
 * This class implements the IAxiosIntegration interface from the centralized types,
 * providing seamless integration between Axios and the unified request coordinator system.
 */
export class AxiosIntegration implements IAxiosIntegration {
  private context: AxiosIntegrationContext;

  constructor(
    client: AxiosInstance,
    config: Partial<AxiosIntegrationConfig> = {},
    authIntegration?: IAuthIntegration
  ) {
    const finalConfig = { ...DEFAULT_AXIOS_INTEGRATION_CONFIG, ...config };

    this.context = {
      coordinator: createRequestCoordinator(
        client,
        finalConfig as CoordinatorConfig,
        authIntegration
      ),
      config: finalConfig,
      client,
      interceptorIds: {
        request: [],
        response: [],
      },
      isSetup: false,
    };
  }

  /**
   * Setup interceptors and integrate coordinator with Axios
   */
  public setupInterceptors(): void {
    if (this.context.isSetup) {
      this.log('Integration already setup, skipping');
      return;
    }

    this.cleanupExistingInterceptors();
    this.setupRequestMetadataTracking();
    this.integrateCoordinator();

    this.context.isSetup = true;
    this.log('Axios integration setup complete');
  }

  /**
   * Create and return a new integrated request coordinator.
   *
   * @param config - Optional coordinator configuration
   * @returns A new request coordinator instance
   */
  public createRequestCoordinator(
    config?: Partial<ICoordinatorConfig>
  ): IRequestCoordinator {
    const finalConfig = config
      ? { ...this.context.config, ...config }
      : this.context.config;
    return createRequestCoordinator(
      this.context.client,
      finalConfig as CoordinatorConfig
    );
  }

  /**
   * Preserve existing behavior while integrating new system
   */
  public preserveExistingBehavior(): void {
    this.log('Preserving existing behavior - no legacy compatibility needed');
    // Since user specified no backward compatibility, this is a no-op
    // All behavior will go through the new system
  }

  /**
   * Update integration configuration
   */
  public updateConfig(config: Partial<AxiosIntegrationConfig>): void {
    this.context.config = { ...this.context.config, ...config };
    this.context.coordinator.updateConfig(config);
    this.log('Configuration updated', config);
  }

  /**
   * Get current integration metrics.
   *
   * Projected from the coordinator, which is the single source of truth for
   * request metrics now that all traffic flows through it.
   *
   * @returns The current metrics summary
   */
  public getMetrics(): IAxiosIntegrationMetrics {
    return { ...this.context.coordinator.getMetrics().summary };
  }

  /**
   * Get active requests.
   *
   * Delegates to the coordinator's request tracker, which owns request
   * lifecycle state.
   */
  public getActiveRequests(): RequestContext[] {
    return this.context.coordinator.getActiveRequests();
  }

  /**
   * Get the internal request coordinator.
   *
   * Use this to subscribe to coordinator notifications or access
   * coordinator features. Note: createRequestCoordinator() creates
   * a NEW coordinator, while this returns the actual internal coordinator
   * that handles requests.
   *
   * @returns The internal request coordinator instance
   */
  public getCoordinator(): IRequestCoordinator {
    return this.context.coordinator;
  }

  /**
   * Cleanup integration and remove interceptors
   */
  public cleanup(): void {
    this.cleanupExistingInterceptors();
    this.context.isSetup = false;
    this.log('Integration cleanup complete');
  }

  // =============================================================================
  // PRIVATE METHODS
  // =============================================================================

  private cleanupExistingInterceptors(): void {
    // Remove any existing interceptors
    this.context.interceptorIds.request.forEach((id) => {
      this.context.client.interceptors.request.eject(id);
    });

    this.context.interceptorIds.response.forEach((id) => {
      this.context.client.interceptors.response.eject(id);
    });

    this.context.interceptorIds.request = [];
    this.context.interceptorIds.response = [];
  }

  /**
   * Setup request interceptor to add metadata tracking to all requests
   */
  private setupRequestMetadataTracking(): void {
    const requestId = this.context.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => this.handleRequest(config),
      (error) => this.handleRequestError(error)
    );

    this.context.interceptorIds.request.push(requestId);
  }

  private async handleRequest(
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> {
    // Thin metadata stamping only. Request lifecycle, metrics, and error/retry
    // handling are owned entirely by the coordinator (executeRequest). This
    // interceptor is a defensive safety net that guarantees every dispatched
    // request carries coordination metadata + an X-Request-ID correlation
    // header — including any request that reaches axios without going through
    // executeRequest first. It is idempotent: requests that already have
    // metadata (the common case, stamped by the coordinator) are preserved.
    const metadata = config.metadata
      ? {
          requestId: config.metadata.requestId,
          timestamp: config.metadata.timestamp || dayjs().valueOf(),
          skipAuth: config.metadata.skipAuth || false,
          retryCount: config.metadata.retryCount || 0,
          isRetry: config.metadata.isRetry || false,
          parentRequestId: config.metadata.parentRequestId,
          startedAt: config.metadata.startedAt,
        }
      : {
          requestId: `req_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`,
          timestamp: dayjs().valueOf(),
          skipAuth: false,
          retryCount: 0,
          isRetry: false,
        };

    const requestConfig: RequestConfig = {
      ...config,
      metadata,
    };

    requestConfig.headers = {
      ...requestConfig.headers,
      'X-Request-ID': metadata.requestId,
    };

    this.log('Stamped request metadata', {
      id: metadata.requestId,
      method: config.method,
      url: config.url,
    });

    return requestConfig as InternalAxiosRequestConfig;
  }

  private handleRequestError(error: unknown): Promise<never> {
    this.log('Request interceptor error', error);

    // Report to observability system
    const observability = getObservability();
    if (error instanceof Error) {
      observability.captureError(error, ObservabilitySeverity.ERROR, {
        component: 'AxiosIntegration',
        phase: 'request-interceptor',
      });
    }

    return Promise.reject(error);
  }

  private log(message: string, data?: unknown): void {
    if (this.context.config.debug) {
      console.log(`[AxiosIntegration] ${message}`, data || '');
    }
  }

  /**
   * Integrate the coordinator by routing all axios requests through it.
   * This enables deduplication, retry logic, circuit breaker, and error classification
   * for all requests while respecting excludeEndpoints configuration.
   */
  private integrateCoordinator(): void {
    // Guard: Skip if client doesn't have a request method (e.g., mock clients in tests)
    if (
      !this.context.client.request ||
      typeof this.context.client.request !== 'function'
    ) {
      const warningMessage =
        'Skipping request override: client.request is not a function';
      this.log(warningMessage);

      // Report to observability system
      const observability = getObservability();
      observability.warning(warningMessage, {
        clientType: typeof this.context.client,
        hasRequest: 'request' in this.context.client,
        requestType: typeof this.context.client.request,
        environment: getEnv('NODE_ENV'),
      });

      return;
    }

    const originalRequest = this.context.client.request.bind(
      this.context.client
    );
    const coordinator = this.context.coordinator;

    // Store original request for coordinator to use (avoiding circular calls)
    const clientWithOriginal = this.context.client as AxiosInstanceWithOriginal;
    clientWithOriginal.originalRequest = originalRequest;

    // Override the request method to route through coordinator. Cast because
    // axios 1.19 threads extra D/P generics through request's declared
    // signature; the runtime contract is unchanged.
    this.context.client.request = (async <T = unknown, R = AxiosResponse<T>>(
      config: RequestConfig
    ): Promise<R> => {
      // Check if this is an internal request from the coordinator itself
      const skipCoordinator = config.metadata?.skipDeduplication || false;

      if (skipCoordinator) {
        // Use original method for internal coordinator requests
        return originalRequest(config) as Promise<R>;
      }

      // The session that is asking. Every request is judged against it once
      // it settles, below — after the coordinator's retries and dedup joins,
      // so nothing they do can slip a response past the check.
      const epoch = getSessionEpoch();

      let response: unknown;
      try {
        // Route all requests through coordinator
        // The coordinator will handle:
        // - Deduplication (with excludeEndpoints check)
        // - Retry logic (with excludeEndpoints check)
        // - Circuit breaker
        // - Error classification
        response = await coordinator.executeRequest(config);
      } catch (error) {
        // A failure, too, belongs to the session that asked: surfacing it
        // would show the old account's error in the new one's session.
        if (getSessionEpoch() !== epoch) {
          throw new SessionEndedError(
            { url: config.url, method: config.method },
            { cause: error }
          );
        }
        throw error;
      }

      // The session ended while this was in flight. Whatever came back is the
      // old account's; nothing downstream may write it into a store or cache
      // (docs/roadmap/session-scoped-state.md, D5).
      if (getSessionEpoch() !== epoch) {
        throw new SessionEndedError({ url: config.url, method: config.method });
      }

      return response as R;
    }) as AxiosInstance['request'];

    // CRITICAL: also override the HTTP convenience methods (get/post/...).
    //
    // In axios v1, createInstance pre-binds every prototype method (get, post,
    // etc.) to an INTERNAL context object, not to the returned instance. So
    // `apiClient.get` is `Axios.prototype.get.bind(context)`, and the `this.request`
    // it calls resolves to `context.request` (the original) — NOT the own-property
    // `request` override we just installed on the instance above. Without these
    // overrides, every convenience-method call bypasses the coordinator (and
    // deduplication) entirely. We therefore funnel each alias through the
    // overridden `request`, mirroring axios' own per-method argument shapes.
    const client = this.context.client;

    const methodsWithoutData = ['get', 'delete', 'head', 'options'] as const;
    for (const method of methodsWithoutData) {
      const override = <T = unknown, R = AxiosResponse<T>>(
        url: string,
        config: RequestConfig = {}
      ): Promise<R> =>
        client.request<T, R>({ ...config, method, url }) as Promise<R>;
      client[method] = override as unknown as (typeof client)[typeof method];
    }

    const methodsWithData = ['post', 'put', 'patch'] as const;
    for (const method of methodsWithData) {
      const override = <T = unknown, R = AxiosResponse<T>>(
        url: string,
        data?: unknown,
        config: RequestConfig = {}
      ): Promise<R> =>
        client.request<T, R>({ ...config, method, url, data }) as Promise<R>;
      client[method] = override as unknown as (typeof client)[typeof method];
    }

    this.log(
      'Axios request + convenience methods overridden to route through coordinator'
    );
  }
}

// =============================================================================
// FACTORY FUNCTIONS
// =============================================================================

/**
 * Create an AxiosIntegration instance with default configuration
 */
export function createAxiosIntegration(
  client: AxiosInstance,
  config?: Partial<AxiosIntegrationConfig>,
  authIntegration?: IAuthIntegration
): IAxiosIntegration {
  return new AxiosIntegration(client, config, authIntegration);
}

/**
 * Setup interceptors for an existing Axios instance
 */
export function setupInterceptors(
  client: AxiosInstance,
  config?: Partial<AxiosIntegrationConfig>,
  authIntegration?: IAuthIntegration
): IAxiosIntegration {
  const integration = createAxiosIntegration(client, config, authIntegration);
  integration.setupInterceptors();
  return integration;
}

/**
 * Create a new request coordinator with integration configuration
 */
export function createIntegratedRequestCoordinator(
  client: AxiosInstance,
  config: IntegrationConfig
): IRequestCoordinator {
  const coordinatorConfig: CoordinatorConfig = {
    ...DEFAULT_COORDINATOR_CONFIG,
    enablePerformanceMonitoring: config.preservePerformanceMonitoring,
    enableNotifications: config.preserveNotificationSystem,
    enableLogging: appEnv('development'),
  };

  return createRequestCoordinator(client, coordinatorConfig);
}

/**
 * Preserve existing behavior (no-op since no backward compatibility needed)
 */
export function preserveExistingBehavior(): void {
  // No-op - user specified no backward compatibility needed
  console.log(
    '[AxiosIntegration] No backward compatibility preservation needed'
  );
}

// =============================================================================
// INTEGRATION UTILITIES
// =============================================================================

/**
 * Check if integration is properly setup on an Axios instance
 */
export function isIntegrationSetup(client: AxiosInstance): boolean {
  // Check if our interceptors are present by checking interceptor count
  return (
    (client.interceptors.request as unknown as { handlers: unknown[] }).handlers
      ?.length > 0
  );
}

/**
 * Get integration context from an Axios instance
 */
export function getIntegrationContext(): AxiosIntegrationContext | null {
  // Since we don't store context on the client, this would need to be tracked externally
  // For now, return null - this could be enhanced if needed
  return null;
}

/**
 * Create integration configuration with sensible defaults
 */
export function createIntegrationConfig(
  overrides?: Partial<IntegrationConfig>
): IntegrationConfig {
  return {
    ...DEFAULT_INTEGRATION_CONFIG,
    ...overrides,
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export default AxiosIntegration;
